import { ControllerFetchType } from "../../helpers/fetchTypes";
import { wealthProApi } from "../../redux/api";
import { useCacheKeyMergeHandler, useCacheKeySerializeQueryArgsHandler, useCacheKeyTransformResponseHandler } from "../../redux/helpers";

const reportsBaseUrl = '/api/reports/';

const buildFetchQueryString = (baseUrl, fetchType, searchParams) => {
    const searchParamObject = new URLSearchParams(
        Object.entries(searchParams)
            .filter(x => x[1] != null)
    );

    let url = reportsBaseUrl + baseUrl;
    if (fetchType === ControllerFetchType.List)
        url += `/list`

    if (searchParamObject.size > 0)
        url += `?${searchParamObject.toString()}`;

    return url;
}

const onUpdateQueryStarted = async ({ standardParagraphId }, { dispatch, queryFulfilled }) => {
    try {
        const { data } = await queryFulfilled;

        dispatch(wealthProApi.util.updateQueryData('fetchStandardParagraphs', {}, (draft) => {
            let { results } = draft;
            const updateIndex = results.findIndex(para => para.standardParagraphId === standardParagraphId);

            if (updateIndex === -1)
                return draft;

            results[updateIndex] = data;
        }));
    } catch (error) {
        console.error(error);
    }
}

const standardParagraphEndpoints = wealthProApi.enhanceEndpoints({
    addTagTypes: ['StandardParagraphs']
}).injectEndpoints({
    endpoints: (builder) => ({
        fetchStandardParagraphTypes: builder.query({
            query: ({ fetchType = ControllerFetchType.Object, ...searchParams }) => buildFetchQueryString('standard-paragraph-types', fetchType, searchParams)
        }),
        createStandardParagraph: builder.mutation({
            query: (body) => ({
                url: '/api/reports/standard-paragraphs',
                method: 'POST',
                body
            }),
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                try {
                    // Add to the totalCount to add a loading row (if on the last page)
                    const addEmptyRowResult = dispatch(wealthProApi.util.updateQueryData('fetchStandardParagraphs', {}, (draft) => ({
                        ...draft,
                        pagination: {
                            ...draft.pagination,
                            totalCount: draft.pagination.totalCount + 1
                        }
                    })));

                    try {
                        const { data } = await queryFulfilled;

                        dispatch(wealthProApi.util.updateQueryData('fetchStandardParagraphs', {}, (draft) => ({
                            ...draft,
                            // Now add the DTO to the end if we're on the last page
                            results: draft.pagination.page === draft.pagination.totalPages
                                ? [...draft.results, data]
                                : draft.results
                        })));
                    } catch (error) {
                        console.error(error);
                        addEmptyRowResult.undo();
                    }
                } catch (error) {
                    console.error(error);
                    console.error("Error creating standard paragraph");
                }
            }
        }),
        fetchStandardParagraphs: builder.query({
            query: (body) => ({
                url: 'api/reports/standard-paragraphs/search',
                method: 'POST',
                body
            }),
            merge: useCacheKeyMergeHandler,
            serializeQueryArgs: useCacheKeySerializeQueryArgsHandler,
            transformResponse: useCacheKeyTransformResponseHandler
        }),
        openStandardParagraph: builder.mutation({
            query: ({ standardParagraphId }) => ({
                url: `api/reports/standard-paragraphs/${standardParagraphId}`,
                method: 'PATCH'
            }),
            onQueryStarted: onUpdateQueryStarted
        }),
        publishStandardParagraph: builder.mutation({
            query: ({ standardParagraphId }) => ({
                url: `api/reports/standard-paragraphs/${standardParagraphId}`,
                method: 'POST'
            }),
            onQueryStarted: onUpdateQueryStarted
        }),
        deleteStandardParagraph: builder.mutation({
            query: ({ standardParagraphId }) => ({
                url: `api/reports/standard-paragraphs/${standardParagraphId}`,
                method: 'DELETE'
            }),
            onQueryStarted: async ({ standardParagraphId }, { dispatch, queryFulfilled }) => {
                try {
                    // First update list by replacing item with null. Should cause loading row in place of deleted row.
                    const deleteResult = dispatch(wealthProApi.util.updateQueryData('fetchStandardParagraphs', {}, (draft) => {
                        const { results } = draft;
                        const deleteIndex = results.findIndex(para => para.standardParagraphId === standardParagraphId);

                        if (deleteIndex === -1)
                            return draft;

                        return {
                            ...draft,
                            results: [
                                ...results.slice(0, deleteIndex),
                                null,
                                ...results.slice(deleteIndex + 1)
                            ]
                        }
                    }));

                    try {
                        await queryFulfilled;

                        // Remove the loading row and decrement totalCount once the request has completed...
                        dispatch(wealthProApi.util.updateQueryData('fetchStandardParagraphs', {}, (draft) => {
                            const { results, pagination } = draft;
                            const { totalCount } = pagination;

                            const deleteIndex = results.findIndex(para => para == null);

                            return {
                                ...draft,
                                results: (
                                    deleteIndex === -1
                                        ? results
                                        : [
                                            ...results.slice(0, deleteIndex),
                                            ...results.slice(deleteIndex + 1)
                                        ]
                                ),
                                pagination: {
                                    ...pagination,
                                    totalCount: totalCount - 1
                                }
                            }
                        }))
                    } catch (error) {
                        // ...or undo the array slice operation if the request fails
                        deleteResult.undo();

                        console.error(error);
                        console.error("Error deleting standard paragraph")
                    }
                } catch (error) {
                    console.error(error);
                    console.error("Error deleting standard paragraph in cache");
                }

            }
        })
    })
})

export const {
    useFetchStandardParagraphTypesQuery,
    useLazyFetchStandardParagraphTypesQuery,
    useCreateStandardParagraphMutation,
    useFetchStandardParagraphsQuery,
    useLazyFetchStandardParagraphsQuery,
    useOpenStandardParagraphMutation,
    usePublishStandardParagraphMutation,
    useDeleteStandardParagraphMutation
} = standardParagraphEndpoints